import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createEvalConfig,
  publishEvalConfigDraft,
  setChampionEvalConfig,
  updateEvalConfigDraft,
  updateEvalConfigMeta,
} from '@mutations/evalConfigs';
import type { UpdateEvalConfigMetaPayload } from '@root/domain/schemas/payload.schema';
import { useQueryClient } from '@tanstack/react-query';

import { EVAL_CONFIGS_QUERY_KEY, getEvalConfigQueryKey } from '../constants';
import type { CreateEvalConfigFormState, EvalConfigDraftState } from '../types';

const toastError = (err: unknown, fallback: string) =>
  toast.error(err instanceof Error ? err.message : fallback, { autoClose: 5000 });

export function useCreateEvalConfigMutation({ onSuccess }: { onSuccess: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const onCreate = useCallback(
    async (data: CreateEvalConfigFormState) => {
      setIsLoading(true);
      try {
        const { id } = await createEvalConfig({
          name: data.name,
          description: data.description,
          workflow_type: data.workflow_type,
          model_id: data.model_id,
          // BLANK is the server default; only a real starter travels.
          ...(data.copy_from !== 'BLANK' ? { copy_from: data.copy_from } : {}),
        });
        await queryClient.invalidateQueries({ queryKey: EVAL_CONFIGS_QUERY_KEY });
        toast.success('Eval config created', { autoClose: 5000 });
        onSuccess(id);
      } catch (err) {
        toastError(err, 'Failed to create eval config');
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, queryClient],
  );

  return { onCreate, isLoading };
}

/**
 * List-page action: promote one config to champion. Only one config can hold
 * the flag, so the previous champion is demoted server-side — refreshing the
 * whole list is what makes both rows come back correct.
 */
export function useSetChampionEvalConfigMutation() {
  const queryClient = useQueryClient();
  const [settingChampionId, setSettingChampionId] = useState<string | undefined>(undefined);

  const onSetChampion = useCallback(
    async (id: string) => {
      setSettingChampionId(id);
      try {
        await setChampionEvalConfig(id);
        await queryClient.invalidateQueries({ queryKey: EVAL_CONFIGS_QUERY_KEY });
        toast.success('Champion updated', { autoClose: 5000 });
      } catch (err) {
        toastError(err, 'Failed to set champion');
      } finally {
        setSettingChampionId(undefined);
      }
    },
    [queryClient],
  );

  return { onSetChampion, settingChampionId };
}

export function useEvalConfigMutations(id: string) {
  const queryClient = useQueryClient();
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getEvalConfigQueryKey(id) }),
      queryClient.invalidateQueries({ queryKey: EVAL_CONFIGS_QUERY_KEY }),
    ]);
  }, [queryClient, id]);

  const onSaveMeta = useCallback(
    async (payload: Omit<UpdateEvalConfigMetaPayload, 'operation'>) => {
      setIsSavingMeta(true);
      try {
        await updateEvalConfigMeta(id, payload);
        await invalidate();
        toast.success('Settings saved', { autoClose: 5000 });
      } catch (err) {
        toastError(err, 'Failed to save settings');
        throw err;
      } finally {
        setIsSavingMeta(false);
      }
    },
    [id, invalidate],
  );

  const onSaveDraft = useCallback(
    async (draft: EvalConfigDraftState) => {
      setIsSavingDraft(true);
      try {
        await updateEvalConfigDraft(id, draft);
        await invalidate();
        toast.success('Draft saved', { autoClose: 5000 });
      } catch (err) {
        toastError(err, 'Failed to save draft');
        throw err;
      } finally {
        setIsSavingDraft(false);
      }
    },
    [id, invalidate],
  );

  const onPublish = useCallback(
    async (changeNote?: string) => {
      setIsPublishing(true);
      try {
        await publishEvalConfigDraft(id, { change_note: changeNote });
        await invalidate();
        toast.success('Draft published', { autoClose: 5000 });
      } catch (err) {
        toastError(err, 'Failed to publish');
        throw err;
      } finally {
        setIsPublishing(false);
      }
    },
    [id, invalidate],
  );

  return {
    onSaveMeta,
    onSaveDraft,
    onPublish,
    isSavingMeta,
    isSavingDraft,
    isPublishing,
  };
}
